import { useEffect, useState } from 'react';
import { AdminSidebar, Icon } from '../../components/shared';
import MapaRaioEntrega from '../../components/MapaRaioEntrega';
import { geocodeEndereco } from '../../utils/geo';
import api from '../../services/api';
import toast from 'react-hot-toast';

const DIAS = [
  { key: 'segunda', label: 'Segunda-feira' },
  { key: 'terca',   label: 'Terça-feira' },
  { key: 'quarta',  label: 'Quarta-feira' },
  { key: 'quinta',  label: 'Quinta-feira' },
  { key: 'sexta',   label: 'Sexta-feira' },
  { key: 'sabado',  label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

const HORARIO_DIA = { aberto: true, abertura: '08:00', fechamento: '22:00' };

const FUSOS = [
  { valor: 'America/Sao_Paulo',   label: 'Brasília / SP / RJ / Sul e Sudeste  (UTC−3/−2)' },
  { valor: 'America/Fortaleza',   label: 'Nordeste — CE, RN, PB, PE, AL, SE, BA  (UTC−3)' },
  { valor: 'America/Belem',       label: 'Pará / Amapá / Maranhão / Piauí  (UTC−3)' },
  { valor: 'America/Manaus',      label: 'Amazonas / Manaus  (UTC−4)' },
  { valor: 'America/Cuiaba',      label: 'Mato Grosso / MS  (UTC−4/−3)' },
  { valor: 'America/Porto_Velho', label: 'Rondônia  (UTC−4)' },
  { valor: 'America/Boa_Vista',   label: 'Roraima  (UTC−4)' },
  { valor: 'America/Rio_Branco',  label: 'Acre  (UTC−5)' },
  { valor: 'America/Noronha',     label: 'Fernando de Noronha  (UTC−2)' },
];

const ABAS = [
  { key: 'horarios',  label: '🕐 Horários' },
  { key: 'entrega',   label: '🛵 Entrega' },
  { key: 'pagamento', label: '💳 Pagamento' },
  { key: 'pedidos',   label: '📋 Pedidos' },
  { key: 'visual',    label: '🖼 Visual' },
];

const WD_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const DIAS_SEMANA = ['domingo','segunda','terca','quarta','quinta','sexta','sabado'];

function statusAtual(cfg) {
  if (!cfg) return null;
  if (cfg.modo_abertura === 'aberto')  return { aberta: true,  label: 'Aberta (manual)' };
  if (cfg.modo_abertura === 'fechado') return { aberta: false, label: 'Fechada (manual)' };

  const fuso  = cfg.fuso_horario || 'America/Sao_Paulo';
  const agora = new Date();
  const wdShort = new Intl.DateTimeFormat('en-US', { timeZone: fuso, weekday: 'short' }).format(agora);
  const dia     = DIAS_SEMANA[WD_MAP[wdShort]];
  const hora    = new Intl.DateTimeFormat('en-GB', { timeZone: fuso, hour: '2-digit', minute: '2-digit', hour12: false }).format(agora);

  const h = cfg.horarios?.[dia];
  if (!h || !h.aberto) return { aberta: false, label: `Fechada hoje (${DIAS.find(d=>d.key===dia)?.label})` };
  const aberta = hora >= h.abertura && hora < h.fechamento;
  return { aberta, label: aberta ? `Aberta até ${h.fechamento}` : `Fechada · abre às ${h.abertura}` };
}

export default function AdminConfiguracoes() {
  const [cfg, setCfg]           = useState(null);
  const [loading, setLoad]      = useState(true);
  const [saving, setSaving]     = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [banners, setBanners] = useState([]);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [buscaEndereco, setBuscaEndereco] = useState('');
  const [buscandoLocal, setBuscandoLocal] = useState(false);
  const [aba, setAba] = useState('horarios');

  useEffect(() => {
    Promise.all([
      api.get('/configuracoes'),
      api.get('/configuracoes/banners'),
    ])
      .then(([cfgRes, bannersRes]) => { setCfg(cfgRes.data); setBanners(bannersRes.data); })
      .catch(() => toast.error('Erro ao carregar configurações'))
      .finally(() => setLoad(false));
  }, []);

  const setHorario = (dia, campo, valor) => {
    setCfg(c => ({
      ...c,
      horarios: { ...c.horarios, [dia]: { ...(c.horarios[dia] || HORARIO_DIA), [campo]: valor } },
    }));
  };

  const setCopiarPara = (diaOrigem) => {
    const h = cfg.horarios[diaOrigem];
    if (!h) return;
    const novos = {};
    DIAS.forEach(d => { novos[d.key] = { ...h }; });
    setCfg(c => ({ ...c, horarios: novos }));
    toast.success('Horário copiado para todos os dias');
  };

  const setMarker = (lat, lng) => setCfg(c => ({ ...c, latitude: lat, longitude: lng }));

  const buscarNoMapa = async () => {
    if (!buscaEndereco.trim()) return;
    setBuscandoLocal(true);
    try {
      const local = await geocodeEndereco(buscaEndereco);
      if (!local) { toast.error('Endereço não encontrado'); return; }
      setMarker(local.lat, local.lng);
    } catch {
      toast.error('Não foi possível buscar o endereço');
    } finally {
      setBuscandoLocal(false);
    }
  };

  const usarLocalizacaoAtual = () => {
    if (!navigator.geolocation) { toast.error('Geolocalização não suportada neste navegador'); return; }
    setBuscandoLocal(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setMarker(pos.coords.latitude, pos.coords.longitude); setBuscandoLocal(false); },
      () => { toast.error('Não foi possível obter sua localização'); setBuscandoLocal(false); }
    );
  };

  const handleBannerUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingBanner(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('banner', file);
        const { data } = await api.post('/configuracoes/banners', formData);
        setBanners(bs => [...bs, data]);
      }
      toast.success(files.length > 1 ? `${files.length} banners adicionados!` : 'Banner adicionado!');
    } catch (err) {
      toast.error(err.response?.data?.erro || err.message || 'Erro ao enviar banner');
    } finally {
      setUploadingBanner(false);
      e.target.value = '';
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await api.post('/configuracoes/logo', formData);
      setCfg(c => ({ ...c, logo_url: data.logo_url }));
      toast.success('Logomarca atualizada!');
    } catch (err) {
      toast.error(err.response?.data?.erro || err.message || 'Erro ao enviar logomarca');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const removerLogo = async () => {
    try {
      await api.delete('/configuracoes/logo');
      setCfg(c => ({ ...c, logo_url: null }));
      toast.success('Logomarca removida!');
    } catch {
      toast.error('Erro ao remover logomarca');
    }
  };

  const removerBanner = async (id) => {
    try {
      await api.delete(`/configuracoes/banners/${id}`);
      setBanners(bs => bs.filter(b => b.id !== id));
      toast.success('Banner removido!');
    } catch {
      toast.error('Erro ao remover banner');
    }
  };

  const salvar = async () => {
    if (cfg.raio_entrega_ativo && (cfg.latitude == null || cfg.longitude == null)) {
      toast.error('Defina a localização da loja no mapa antes de ativar o raio de entrega');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/configuracoes', cfg);
      setCfg(data);
      toast.success('Configurações salvas!');
    } catch {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const status = statusAtual(cfg);

  if (loading) return (
    <div className="admin-layout">
      <AdminSidebar perfil="admin_empresa" />
      <div className="main-content"><div className="loading-center"><div className="spinner" /></div></div>
    </div>
  );

  return (
    <div className="admin-layout">
      <AdminSidebar perfil="admin_empresa" />
      <div className="main-content">
        <div className="topbar">
          <h1>Configurações da Loja</h1>
          <button className="btn btn-primary" onClick={salvar} disabled={saving}>
            {saving ? 'Salvando...' : <><Icon name="check" size={16} /> Salvar</>}
          </button>
        </div>

        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Status atual */}
          {status && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '14px 20px', borderRadius: 'var(--radius-lg)',
              background: status.aberta ? '#dcfce7' : '#fee2e2',
              border: `1px solid ${status.aberta ? '#86efac' : '#fca5a5'}`,
            }}>
              <span style={{ fontSize: '1.4rem' }}>{status.aberta ? '🟢' : '🔴'}</span>
              <div>
                <div style={{ fontWeight: 700, color: status.aberta ? '#166534' : '#991b1b' }}>
                  Loja {status.aberta ? 'ABERTA' : 'FECHADA'}
                </div>
                <div style={{ fontSize: '.82rem', color: status.aberta ? '#166534' : '#991b1b' }}>{status.label}</div>
              </div>
            </div>
          )}

          {/* Abas */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', borderBottom: '1px solid var(--border)', paddingBottom: '2px' }}>
            {ABAS.map(t => (
              <button
                key={t.key}
                onClick={() => setAba(t.key)}
                style={{
                  padding: '10px 18px', borderRadius: 'var(--radius) var(--radius) 0 0',
                  border: 'none', borderBottom: `2px solid ${aba === t.key ? 'var(--primary)' : 'transparent'}`,
                  background: 'transparent',
                  color: aba === t.key ? 'var(--primary)' : 'var(--text-muted)',
                  cursor: 'pointer', fontWeight: 700, fontSize: '.875rem',
                  transition: 'all .15s',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {aba === 'horarios' && <>

          {/* Controle de abertura */}
          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>Controle de Abertura</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {[
                { valor: 'auto',    label: '🕐 Automático (por horário)' },
                { valor: 'aberto',  label: '🟢 Forçar Aberta' },
                { valor: 'fechado', label: '🔴 Forçar Fechada' },
              ].map(op => (
                <button
                  key={op.valor}
                  onClick={() => setCfg(c => ({ ...c, modo_abertura: op.valor }))}
                  style={{
                    padding: '10px 18px', borderRadius: 'var(--radius)', border: '2px solid',
                    borderColor: cfg.modo_abertura === op.valor ? 'var(--primary)' : 'var(--border)',
                    background: cfg.modo_abertura === op.valor ? 'var(--primary)' : 'var(--bg-card)',
                    color: cfg.modo_abertura === op.valor ? '#fff' : 'var(--text)',
                    cursor: 'pointer', fontWeight: 600, fontSize: '.875rem',
                    transition: 'all .15s',
                  }}
                >
                  {op.label}
                </button>
              ))}
            </div>

            {cfg.modo_abertura === 'fechado' && (
              <div className="form-group" style={{ marginTop: '14px', maxWidth: '480px' }}>
                <label className="form-label">Mensagem para clientes (opcional)</label>
                <input
                  className="form-control"
                  placeholder="Ex: Voltamos amanhã às 08h. Obrigado!"
                  value={cfg.mensagem_fechado || ''}
                  onChange={e => setCfg(c => ({ ...c, mensagem_fechado: e.target.value }))}
                />
              </div>
            )}
          </div>

          {/* Fuso horário */}
          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '4px', fontSize: '1rem' }}>Fuso Horário da Loja</h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
              Define o fuso usado para calcular abertura/fechamento automático.
            </p>
            <select
              className="form-control"
              style={{ maxWidth: '480px' }}
              value={cfg.fuso_horario || 'America/Sao_Paulo'}
              onChange={e => setCfg(c => ({ ...c, fuso_horario: e.target.value }))}
            >
              {FUSOS.map(f => (
                <option key={f.valor} value={f.valor}>{f.label}</option>
              ))}
            </select>
          </div>

          {/* Horários por dia */}
          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>Horários de Funcionamento</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {DIAS.map(({ key, label }) => {
                const h = cfg.horarios?.[key] || { aberto: false, abertura: '08:00', fechamento: '22:00' };
                return (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
                    padding: '12px 16px', borderRadius: 'var(--radius)',
                    background: h.aberto ? 'var(--bg-card)' : 'var(--secondary)',
                    border: '1px solid var(--border)',
                  }}>
                    {/* Toggle aberto/fechado */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: '160px' }}>
                      <div
                        onClick={() => setHorario(key, 'aberto', !h.aberto)}
                        style={{
                          width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                          background: h.aberto ? 'var(--primary)' : '#d1d5db',
                          position: 'relative', transition: 'background .2s',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: 3, left: h.aberto ? 21 : 3,
                          width: 16, height: 16, borderRadius: '50%', background: '#fff',
                          transition: 'left .2s',
                        }} />
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '.875rem', userSelect: 'none' }}>{label}</span>
                    </label>

                    {h.aberto ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '.875rem' }}>
                          <label style={{ color: 'var(--text-muted)' }}>Abre:</label>
                          <input
                            type="time"
                            className="form-control"
                            value={h.abertura}
                            onChange={e => setHorario(key, 'abertura', e.target.value)}
                            style={{ width: '110px' }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '.875rem' }}>
                          <label style={{ color: 'var(--text-muted)' }}>Fecha:</label>
                          <input
                            type="time"
                            className="form-control"
                            value={h.fechamento}
                            onChange={e => setHorario(key, 'fechamento', e.target.value)}
                            style={{ width: '110px' }}
                          />
                        </div>
                        <button
                          className="btn btn-sm"
                          onClick={() => setCopiarPara(key)}
                          title="Copiar este horário para todos os dias"
                          style={{ fontSize: '.75rem', padding: '4px 10px', marginLeft: 'auto' }}
                        >
                          Copiar para todos
                        </button>
                      </>
                    ) : (
                      <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>Fechado</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          </>}

          {aba === 'entrega' && <>

          {/* Tipos de entrega */}
          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '6px', fontSize: '1rem' }}>Opções de Entrega</h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
              Selecione quais modalidades estarão disponíveis para o cliente ao fazer o pedido.
            </p>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {[
                { valor: 'entrega',  label: '🛵 Entrega',          desc: 'Entregamos no endereço do cliente' },
                { valor: 'retirada', label: '🏪 Retirada no local', desc: 'Cliente retira no estabelecimento' },
                { valor: 'mesa',     label: '🪑 Consumo na mesa',   desc: 'Pedido feito pela mesa' },
              ].map(op => {
                const tipos = cfg.tipos_entrega || [];
                const ativo = tipos.includes(op.valor);
                const toggle = () => {
                  const novos = ativo
                    ? tipos.filter(t => t !== op.valor)
                    : [...tipos, op.valor];
                  if (novos.length === 0) return toast.error('Selecione pelo menos uma opção');
                  setCfg(c => ({ ...c, tipos_entrega: novos }));
                };
                return (
                  <div
                    key={op.valor}
                    onClick={toggle}
                    style={{
                      cursor: 'pointer', padding: '16px 20px', borderRadius: 'var(--radius-lg)',
                      border: `2px solid ${ativo ? 'var(--primary)' : 'var(--border)'}`,
                      background: ativo ? 'rgba(255,107,53,.06)' : 'var(--bg-card)',
                      minWidth: '200px', transition: 'all .15s', userSelect: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: `2px solid ${ativo ? 'var(--primary)' : 'var(--border)'}`,
                        background: ativo ? 'var(--primary)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {ativo && <Icon name="check" size={11} style={{ color: '#fff' }} />}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: '.9rem' }}>{op.label}</span>
                    </div>
                    <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: 0, paddingLeft: '30px' }}>
                      {op.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Raio de entrega */}
          {(cfg.tipos_entrega || []).includes('entrega') && (
            <div className="table-card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ fontSize: '1rem', margin: 0 }}>Raio de Entrega</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div
                    onClick={() => setCfg(c => ({ ...c, raio_entrega_ativo: !c.raio_entrega_ativo }))}
                    style={{
                      width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
                      background: cfg.raio_entrega_ativo ? 'var(--primary)' : '#d1d5db',
                      position: 'relative', transition: 'background .2s',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3, left: cfg.raio_entrega_ativo ? 21 : 3,
                      width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      transition: 'left .2s',
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: '.875rem' }}>
                    {cfg.raio_entrega_ativo ? 'Ativado' : 'Desativado'}
                  </span>
                </label>
              </div>
              <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
                Se ativado, pedidos de entrega só serão aceitos se o endereço do cliente estiver dentro do raio definido a partir do ponto marcado no mapa.
              </p>

              {cfg.raio_entrega_ativo && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div className="form-group" style={{ maxWidth: '220px' }}>
                    <label className="form-label">Raio de entrega (km)</label>
                    <input
                      type="number" min="0.5" step="0.5" className="form-control"
                      value={cfg.raio_entrega_km || 5}
                      onChange={e => setCfg(c => ({ ...c, raio_entrega_km: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1, minWidth: '220px', marginBottom: 0 }}>
                      <label className="form-label">Buscar endereço da loja</label>
                      <input
                        className="form-control"
                        placeholder="Ex: Rua Exemplo, 123 - Bairro, Cidade"
                        value={buscaEndereco}
                        onChange={e => setBuscaEndereco(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && buscarNoMapa()}
                      />
                    </div>
                    <button className="btn" onClick={buscarNoMapa} disabled={buscandoLocal}>
                      {buscandoLocal ? 'Buscando...' : '🔍 Buscar no mapa'}
                    </button>
                    <button className="btn" onClick={usarLocalizacaoAtual} disabled={buscandoLocal}>
                      📍 Usar minha localização
                    </button>
                  </div>

                  <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: 0 }}>
                    Clique no mapa ou arraste o marcador para ajustar o ponto central da loja.
                  </p>

                  <MapaRaioEntrega
                    markerPos={cfg.latitude != null && cfg.longitude != null ? [cfg.latitude, cfg.longitude] : null}
                    onMarkerChange={setMarker}
                    radiusKm={cfg.raio_entrega_km}
                    editable
                  />

                  {(cfg.latitude == null || cfg.longitude == null) && (
                    <div style={{ fontSize: '.82rem', color: '#b91c1c' }}>
                      ⚠️ Nenhuma localização definida ainda. Busque um endereço, use sua localização atual ou clique no mapa.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          </>}

          {aba === 'pagamento' && <>

          {/* Formas de pagamento */}
          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '6px', fontSize: '1rem' }}>Formas de Pagamento</h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
              Selecione quais formas de pagamento são aceitas na sua loja.
            </p>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {[
                { valor: 'dinheiro',       label: '💵 Dinheiro',         desc: 'Pagamento em espécie' },
                { valor: 'cartao',         label: '💳 Cartão',           desc: 'Débito e crédito' },
                { valor: 'pix',            label: '⚡ PIX',              desc: 'Transferência instantânea' },
                { valor: 'vale_refeicao',  label: '🍽 Vale Refeição',    desc: 'VR, Sodexo, Alelo etc.' },
                { valor: 'vale_alimentacao', label: '🛒 Vale Alimentação', desc: 'VA, Ticket etc.' },
                { valor: 'online',         label: '🌐 Pagamento Online',  desc: 'Link de pagamento' },
              ].map(op => {
                const formas = cfg.formas_pagamento || [];
                const ativo  = formas.includes(op.valor);
                const toggle = () => {
                  const novas = ativo
                    ? formas.filter(f => f !== op.valor)
                    : [...formas, op.valor];
                  if (novas.length === 0) return toast.error('Selecione pelo menos uma forma de pagamento');
                  setCfg(c => ({ ...c, formas_pagamento: novas }));
                };
                return (
                  <div
                    key={op.valor}
                    onClick={toggle}
                    style={{
                      cursor: 'pointer', padding: '16px 20px', borderRadius: 'var(--radius-lg)',
                      border: `2px solid ${ativo ? 'var(--primary)' : 'var(--border)'}`,
                      background: ativo ? 'rgba(255,107,53,.06)' : 'var(--bg-card)',
                      minWidth: '190px', transition: 'all .15s', userSelect: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: `2px solid ${ativo ? 'var(--primary)' : 'var(--border)'}`,
                        background: ativo ? 'var(--primary)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {ativo && <Icon name="check" size={11} />}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: '.9rem' }}>{op.label}</span>
                    </div>
                    <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', margin: 0, paddingLeft: '30px' }}>
                      {op.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          </>}

          {aba === 'pedidos' && <>

          {/* Configurações gerais */}
          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>Configurações de Pedido</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Tempo estimado de entrega (min)</label>
                <input
                  type="number" min="1" className="form-control"
                  value={cfg.tempo_entrega_min || 45}
                  onChange={e => setCfg(c => ({ ...c, tempo_entrega_min: parseInt(e.target.value) }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Valor mínimo do pedido (R$)</label>
                <input
                  type="number" min="0" step="0.01" className="form-control"
                  value={cfg.valor_minimo_pedido || 0}
                  onChange={e => setCfg(c => ({ ...c, valor_minimo_pedido: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Aceitar pedidos online</label>
                <select
                  className="form-control"
                  value={cfg.aceita_pedidos ? 'sim' : 'nao'}
                  onChange={e => setCfg(c => ({ ...c, aceita_pedidos: e.target.value === 'sim' }))}
                >
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>
            </div>
          </div>

          </>}

          {aba === 'visual' && <>

          <div className="table-card" style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '6px', fontSize: '1rem' }}>Logomarca</h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Exibida no cabeçalho do cardápio e no rodapé. Recomendado: quadrada, pelo menos 200×200px (PNG com fundo transparente ou JPG).
            </p>

            {cfg.logo_url ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                <img
                  src={`${process.env.REACT_APP_API_BASE || 'http://localhost:3001'}${cfg.logo_url}`}
                  alt="Logomarca atual"
                  style={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: '#f5f5f5', padding: 8 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label style={{ cursor: 'pointer' }}>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                    <span className="btn btn-sm">{uploadingLogo ? 'Enviando...' : '🔄 Trocar logomarca'}</span>
                  </label>
                  <button className="btn btn-sm" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={removerLogo}>
                    🗑 Remover logomarca
                  </button>
                </div>
              </div>
            ) : (
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 12, padding: '40px 20px', border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)',
                cursor: 'pointer', background: 'var(--secondary)', transition: 'border-color .15s',
              }}
                onMouseOver={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                <span style={{ fontSize: '2.5rem' }}>🏷</span>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {uploadingLogo ? 'Enviando...' : 'Clique para carregar a logomarca'}
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>PNG (recomendado), JPG ou WEBP · máx. 5MB</div>
                </div>
              </label>
            )}
          </div>

          <div className="table-card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>Banners da Loja</h3>
              <label style={{ cursor: 'pointer' }}>
                <input type="file" accept="image/*" multiple onChange={handleBannerUpload} style={{ display: 'none' }} />
                <span className="btn btn-sm btn-primary">{uploadingBanner ? 'Enviando...' : '+ Adicionar banner'}</span>
              </label>
            </div>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Exibidos em carrossel no topo do cardápio. Recomendado: 1200×400px (JPG, PNG ou WEBP). Selecione vários arquivos de uma vez para carregar em lote.
            </p>

            {banners.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                {banners.map((b, i) => (
                  <div key={b.id} style={{ position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <img
                      src={`${process.env.REACT_APP_API_BASE || 'http://localhost:3001'}${b.url}`}
                      alt={`Banner ${i + 1}`}
                      style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{ position: 'absolute', top: 0, right: 0, left: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: 'linear-gradient(to bottom, rgba(0,0,0,.55), transparent)' }}>
                      <span style={{ color: '#fff', fontSize: '.72rem', fontWeight: 700 }}>#{i + 1}</span>
                      <button onClick={() => removerBanner(b.id)}
                        style={{ background: 'rgba(239,68,68,.85)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: '.72rem', fontWeight: 700, padding: '3px 8px' }}>
                        🗑 Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 12, padding: '40px 20px', border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)',
                cursor: 'pointer', background: 'var(--secondary)', transition: 'border-color .15s',
              }}
                onMouseOver={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <input type="file" accept="image/*" multiple onChange={handleBannerUpload} style={{ display: 'none' }} />
                <span style={{ fontSize: '2.5rem' }}>🖼</span>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {uploadingBanner ? 'Enviando...' : 'Clique para carregar banners'}
                  </div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>JPG, PNG ou WEBP · máx. 5MB · pode selecionar vários</div>
                </div>
              </label>
            )}
          </div>

          </>}

        </div>
      </div>
    </div>
  );
}
